import { getLayoverLogger } from '@/logging/LayoverLogger';
import { getLayoverMetrics } from '@/logging/LayoverMetrics';
import { gtag } from '@/analytics/googleAnalytics';
import { isBrowser, isTest } from '@/utils/env';
import { optAppConfig } from '@/utils/config';
import { runWithHeap } from '@/analytics/heapAnalytics';

export const ATTR_TRACK_NAME = 'data-track-name' as const;
export const ATTR_TRACK_PROPS = 'data-track-props' as const;

export type TrackName = string;
export type TrackProp = string | number | boolean;
export type TrackProps = Record<string, TrackProp | Record<string, TrackProp>>; // Max 2 levels deep

// HTML attributes for tracking data
export type TrackHTMLAttrs<TTrackName> = {
  [ATTR_TRACK_NAME]: TTrackName;
  [ATTR_TRACK_PROPS]?: string;
};

/**
 * Add attributes to element for tracking
 *
 * NOTE: Use the mk*TrackAttrs functions in the respective prop file, not this function directly
 *
 * Example: <div {...mkTrackAttrs('featured_content_item', {type: 'video'})} />
 * Output: <div data-track-name="featured_content_item" data-track-props="{&quot;type&quot;:&quot;video&quot;}" />
 */
export function mkTrackAttrs<TTrackName extends TrackName>(
  name: TTrackName,
  props: TrackProps = {},
): TrackHTMLAttrs<TTrackName> {
  const attrs: TrackHTMLAttrs<TTrackName> = {
    [ATTR_TRACK_NAME]: name,
  };
  if (Object.keys(props).length > 0) {
    attrs[ATTR_TRACK_PROPS] = JSON.stringify(props);
  }
  return attrs;
}

const CONSOLE_LOG_STYLE = `
  background-color: #f8f8f8;
  border-radius: 2px;
  border: 1px solid #ddd;
  color: #888;
  padding: 2px 4px;
`;

// Generic tracking function that sends events to various analytics services
export function track(eventName: string, attrs: Record<string, any>): void {
  const shouldTrack = isBrowser() && !isTest();
  if (!shouldTrack) {
    return;
  }
  if (optAppConfig((_) => _.track.log_to_console)) {
    console.log('%ctrack("%s")', CONSOLE_LOG_STYLE, eventName, attrs);
  }
  getLayoverMetrics().sendIncrement('track.' + eventName);
  getLayoverLogger().log('track__' + eventName, attrs);
  runWithHeap((heap) => {
    heap.track(eventName, attrs);
  });
  gtag(eventName, attrs);
}
